﻿import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import * as React from "react"

import { Badge, Button, Money, PageHeader } from "@/components/ui"
import { ExportButton } from "@/features/exports/export-button"
import { DataTable, useListParams, type Column } from "@/components/data-table"
import { useEvents, type EventOrdering } from "@/features/events/api"
import {
  EventFilters,
  NO_FILTERS,
  type EventFilterState,
} from "@/features/events/event-filters"
import type { EventRow } from "@/features/events/model"
import { PAGE_SIZE, STATUS_LABELS, STATUS_TONES } from "@/lib/constants"
import { useSession } from "@/lib/session"
import { formatDate } from "@/lib/utils"

export const Route = createFileRoute("/events/")({ component: EventsScreen })

function EventsScreen() {
  const navigate = useNavigate()
  const { canWrite } = useSession()
  const { page, setPage, ordering, setOrdering, search, setSearch, debouncedSearch } =
    useListParams<EventOrdering>("-start_at")
  const [filters, setFilters] = React.useState<EventFilterState>(NO_FILTERS)

  const query = useEvents({
    page,
    ordering,
    q: debouncedSearch,
    status: filters.status,
    interventionId: filters.intervention?.id ?? null,
    providerId: filters.provider?.id ?? null,
  })

  // A narrower set rarely has the page you were on, so a filter change starts
  // again at the first one.
  function applyFilters(next: EventFilterState) {
    setFilters(next)
    setPage(1)
  }

  const columns: Column<EventRow>[] = [
    {
      key: "event_number",
      header: "Event",
      sortable: true,
      primary: true,
      cell: (row) => (
        <span className="font-mono text-xs font-medium">
          {row.event_number || "Not numbered"}
        </span>
      ),
    },
    {
      key: "intervention_name",
      header: "Intervention",
      cell: (row) => <span className="font-medium">{row.intervention_name}</span>,
    },
    {
      key: "start_at",
      header: "Date",
      sortable: true,
      cell: (row) => formatDate(row.start_at) || "Not scheduled",
    },
    {
      key: "provider_name",
      header: "Provider",
      cell: (row) => row.provider_name || "Not chosen",
    },
    {
      key: "learner_count",
      header: "Learners",
      cell: (row) => row.learner_count,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      cell: (row) => (
        <Badge tone={STATUS_TONES[row.status] ?? "neutral"}>
          {STATUS_LABELS[row.status] ?? row.status}
        </Badge>
      ),
    },
    {
      key: "total_cost",
      header: "Total cost",
      className: "text-right",
      cell: (row) => <Money amount={row.total_cost} className="font-medium" />,
    },
  ]

  // The empty state must answer the question the user actually has: "nothing
  // matches what I asked for" and "nothing has been captured yet" are
  // different facts, and telling a filtering user to create their first event
  // was the defect this branch replaces.
  const filtering = Boolean(
    debouncedSearch || filters.status || filters.intervention || filters.provider,
  )

  // Navigation in a handler rather than a styled Link: the shared Button has no
  // `asChild`, and a button nested inside an anchor is invalid markup.
  const newEventButton = canWrite ? (
    <Button onClick={() => void navigate({ to: "/events/new" })}>
      <Plus className="h-4 w-4" aria-hidden />
      New event
    </Button>
  ) : undefined

  return (
    <>
      <PageHeader
        eyebrow="Training"
        title="Events"
        description="Every occurrence of an intervention, with its learners, costs and status."
        actions={
          <div className="flex items-start gap-2">
            <ExportButton
              slug="events"
              params={{
                q: debouncedSearch,
                status: filters.status,
                intervention_id: filters.intervention?.id ?? null,
                provider_id: filters.provider?.id ?? null,
                ordering,
              }}
            />
            {newEventButton}
          </div>
        }
      />

      <EventFilters value={filters} onChange={applyFilters} />

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        count={query.data?.count ?? 0}
        rowKey={(row) => row.id ?? row.event_number ?? ""}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        ordering={ordering}
        onOrderingChange={setOrdering}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by event number, intervention, provider or venue"
        isLoading={query.isPending}
        isRefreshing={query.isFetching && !query.isPending}
        isError={query.isError}
        onRetry={() => void query.refetch()}
        onRowClick={(row) => {
          if (row.id == null) return
          void navigate({
            to: "/events/$eventId",
            params: { eventId: String(row.id) },
          })
        }}
        emptyTitle={filtering ? "No events match" : "No training events yet"}
        emptyDescription={
          filtering
            ? "Try a different search, or clear the filters."
            : canWrite
              ? "Create the first event and its learner records are generated for you."
              : "No training has been captured yet."
        }
        emptyAction={newEventButton}
      />
    </>
  )
}
